// Pantalla intermedia post-pago del plan ULTRA — la llamada de capacitación
// incluida. Tras un pago exitoso de cualquier plan Ultra (`esPlanUltra`), el
// "Continuar" del éxito trae acá antes que a cualquier otra cosa: el plan incluye
// una llamada personalizada de onboarding y éste es el momento de agendarla, con
// la suscripción recién hecha y el usuario todavía en el flujo.
//
// Llegan acá los TRES éxitos de pago: el checkout embebido AR (`Checkout`), el
// alta por link (`EnterpriseCheckout`, planes Ultra Nivel N) y la vuelta del
// redirect de Stripe/MP (`SubscriptionSuccess`, que consulta el plan al click).
//
// La oferta de facturación viene DESPUÉS de esta pantalla (pedido explícito):
// el llamador que la tenía en su camino (el checkout AR) manda `conFacturacion`
// en el state y el "Continuar" de acá la encadena; sin eso, directo al estudio.
//
// También se llega desde el banner "Agendá tu llamada de capacitación" de la
// home (`desdeInicio` en el state — ADR-2026-08-25-banner-llamada-capacitacion-ultra).
//
// Es puro front, como `FacturacionOferta`: no emite ni guarda nada; solo rutea.
// Mismo guard también: sin `postPago`/`desdeInicio` en el state (entrada directa
// por URL o un refresh) va al estudio; `?preview` la fuerza para revisar el
// diseño sin pagar. Ver ADR-2026-08-25-llamada-capacitacion-ultra-post-pago.

import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Video, ArrowUpRight } from 'lucide-react';
import { useUserGroup } from '../hooks/useUserGroup';
import { entryStudioPath } from '../lib/userGroups';

/** Booking page de Google Calendar para agendar la llamada de capacitación. */
const LINK_AGENDA_LLAMADA = 'https://calendar.app.google/VyhCP4bfwcAPWbVH9';

export default function CapacitacionUltra() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isDevTester, grupo } = useUserGroup();
  const homePath = entryStudioPath(isDevTester, grupo);

  const state = location.state as {
    postPago?: boolean;
    /** El llamador ya decidió que la oferta de facturación sigue después de acá
     *  (hoy: el checkout AR, vía `facturacionVisible`). Viaja decidido y no se
     *  recalcula: quién sabe de país y flags es el punto de entrada. */
    conFacturacion?: boolean;
    /** Entró desde el banner de la home, no desde un pago. */
    desdeInicio?: boolean;
  } | null;
  const preview = searchParams.get('preview') !== null;
  const puedeVerse =
    preview || state?.postPago === true || state?.desdeInicio === true;
  useEffect(() => {
    if (!puedeVerse) navigate(homePath, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!puedeVerse) return null;

  // "Agendar videollamada" abre el calendar en otra pestaña (es un link externo), así
  // que el usuario VUELVE a esta pantalla al terminar y sale por "Continuar".
  const continuar = () => {
    if (state?.conFacturacion) {
      navigate('/facturacion-oferta', { replace: true, state: { postPago: true } });
    } else {
      navigate(homePath, { replace: true });
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center overflow-y-auto bg-surface-card px-5 py-10">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-neutral-100 text-brand-primary">
          <Video className="h-7 w-7" />
        </div>
        <h1 className="text-[22px] font-bold leading-tight tracking-tight text-brand-black/90">
          Gracias por suscribirte al Plan Ultra
        </h1>
        <p className="mx-auto mt-2 max-w-sm text-[14px] leading-snug text-neutral-500">
          Este plan incluye una videollamada personalizada donde aprenderás
          cómo sacarle el máximo provecho a Atlas para tu marca o agencia. Agendá
          para el día que te quede más cómodo.
        </p>

        <div className="mt-7 flex flex-col gap-2.5">
          <a
            href={LINK_AGENDA_LLAMADA}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-11 items-center justify-center gap-1.5 rounded-md bg-brand-primary px-5 text-[15px] font-medium text-white transition-colors hover:bg-brand-primary-hover"
          >
            Agendar videollamada <ArrowUpRight className="h-4 w-4" />
          </a>
          <button
            type="button"
            onClick={continuar}
            className="inline-flex h-11 items-center justify-center rounded-md px-5 text-[14px] font-medium text-neutral-500 transition-colors hover:text-brand-black/80"
          >
            Continuar
          </button>
        </div>
      </div>
    </div>
  );
}
